import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, BookMarked, FileText, Package, CheckCircle2, XCircle } from "lucide-react";
import { adminGet } from "@/lib/adminApi";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
function fmtEuro(n) { return `€ ${Number(n || 0).toFixed(2)}`; }

const REQUEST_STATUSES = [
  { id: "pending", label: "In attesa", color: "#E89B6C" },
  { id: "approved", label: "Approvata", color: "#3A4F6B" },
  { id: "shipped", label: "Spedita", color: "#0E7C8F" },
  { id: "delivered", label: "Consegnata", color: "#0B7C5C" },
  { id: "cancelled", label: "Annullata", color: "#B23A48" },
];

export default function BookstoresTab({ token }) {
  const [tab, setTab] = useState("bookstores"); // bookstores | modules | requests
  const [loading, setLoading] = useState(true);
  const [bookstores, setBookstores] = useState([]);
  const [modules, setModules] = useState([]);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [bs, mm, rr] = await Promise.all([
        adminGet("/admin/bookstores"),
        adminGet("/admin/bookstore-modules"),
        adminGet("/admin/bookstore-requests"),
      ]);
      setBookstores(bs.items || []);
      setModules(mm.items || []);
      setRequests(rr.items || []);
    } catch (err) {
      toast.error("Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateRequestStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookstore-request/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Stato aggiornato");
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const toggleBookstore = async (id) => {
    try {
      await axios.patch(`${API}/admin/bookstore/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Stato libreria aggiornato");
      load();
    } catch { toast.error("Errore"); }
  };

  if (loading) {
    return <div className="p-10 text-center"><Loader2 className="animate-spin inline" /></div>;
  }

  const bsById = Object.fromEntries(bookstores.map(b => [b.id, b]));

  return (
    <div data-testid="admin-bookstores-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          {[
            { id: "bookstores", label: `Librerie (${bookstores.length})`, icon: BookMarked },
            { id: "modules", label: `Moduli (${modules.length})`, icon: FileText },
            { id: "requests", label: `Richieste (${requests.length})`, icon: Package },
          ].map(t => {
            const Ic = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-bs-subtab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] border ${
                  tab === t.id
                    ? "bg-[#0B192C] text-[#F9F8F6] border-[#0B192C]"
                    : "bg-white text-[#0B192C] border-[#E5E1DA]"
                }`}
              >
                <Ic size={14} /> {t.label}
              </button>
            );
          })}
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#0E7C8F]">
          <RefreshCw size={14} /> Aggiorna
        </button>
      </div>

      {tab === "bookstores" && (
        <div className="bg-white border border-[#E5E1DA]">
          <table className="w-full text-sm">
            <thead className="bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.15em]">
              <tr>
                <th className="text-left px-4 py-3">Libreria</th>
                <th className="text-left px-4 py-3">Referente</th>
                <th className="text-left px-4 py-3">Città</th>
                <th className="text-left px-4 py-3">Contatti</th>
                <th className="text-left px-4 py-3">Codice</th>
                <th className="text-left px-4 py-3">Registrata</th>
                <th className="text-center px-4 py-3">Stato</th>
              </tr>
            </thead>
            <tbody>
              {bookstores.map(b => (
                <tr key={b.id} className="border-t border-[#E5E1DA]" data-testid={`admin-bs-row-${b.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{b.business_name}</div>
                    <div className="text-xs text-[#5A6472]">{b.address}, {b.postal_code} {b.city}</div>
                    {b.vat_number && <div className="text-xs text-[#5A6472]">P.IVA {b.vat_number}</div>}
                  </td>
                  <td className="px-4 py-3">{b.contact_person}</td>
                  <td className="px-4 py-3">{b.city} {b.province ? `(${b.province})` : ""}</td>
                  <td className="px-4 py-3 text-xs">
                    <div>{b.email}</div>
                    <div className="text-[#5A6472]">{b.phone}</div>
                  </td>
                  <td className="px-4 py-3"><code className="text-xs bg-[#F4EDE0] px-2 py-1">{b.referral_code}</code></td>
                  <td className="px-4 py-3 text-xs text-[#5A6472]">{fmtDate(b.created_at)}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleBookstore(b.id)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs ${
                        b.disabled ? "bg-[#B23A48] text-white" : "bg-[#0B7C5C] text-white"
                      }`}
                    >
                      {b.disabled ? <><XCircle size={12}/>Off</> : <><CheckCircle2 size={12}/>Attiva</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "modules" && (
        <div className="space-y-3">
          {modules.map(m => {
            const bs = bsById[m.bookstore_id] || m.bookstore_snapshot || {};
            return (
              <div key={m.id} className="bg-white border border-[#E5E1DA] p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-[#0B192C]">{bs.business_name}</p>
                    <p className="text-xs text-[#5A6472]">Data modulo: {m.module_date} · {m.location} · Registrato {fmtDate(m.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#5A6472]">Quota autore</p>
                    <p className="text-lg font-bold text-[#0E7C8F]">{fmtEuro(m.totals?.author_amount)}</p>
                  </div>
                </div>
                <table className="w-full text-sm mt-2">
                  <thead>
                    <tr className="text-[10px] uppercase text-[#5A6472] border-b border-[#E5E1DA]">
                      <th className="text-left py-2">Titolo</th>
                      <th className="text-right py-2">Prezzo</th>
                      <th className="text-center py-2">Q.tà</th>
                      <th className="text-right py-2">Subtotale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.items.map((it,i) => (
                      <tr key={i} className="border-b border-[#F4EDE0]">
                        <td className="py-2">{it.title}</td>
                        <td className="py-2 text-right font-mono">{fmtEuro(it.price)}</td>
                        <td className="py-2 text-center">{it.quantity}</td>
                        <td className="py-2 text-right font-mono">{fmtEuro(it.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3 pt-3 border-t border-[#E5E1DA] text-xs text-[#5A6472] flex justify-between">
                  <span>Firmato da: {m.signed_by || "—"}</span>
                  <span>Trattenuta libreria: {m.totals?.bookstore_retention_pct}% ({fmtEuro(m.totals?.bookstore_retention_amount)})</span>
                </div>
                {m.notes && <p className="mt-2 text-sm italic text-[#5A6472]">Note: {m.notes}</p>}
              </div>
            );
          })}
        </div>
      )}

      {tab === "requests" && (
        <div className="space-y-3">
          {requests.map(r => {
            const bs = bsById[r.bookstore_id] || r.bookstore_snapshot || {};
            const st = REQUEST_STATUSES.find(s => s.id === r.status) || REQUEST_STATUSES[0];
            return (
              <div key={r.id} className="bg-white border border-[#E5E1DA] p-5" data-testid={`admin-req-${r.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-[#0B192C]">{bs.business_name}</p>
                    <p className="text-xs text-[#5A6472]">{bs.contact_person} · {bs.phone} · {fmtDate(r.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-[#0B192C]">{r.quantity}</p>
                    <p className="text-xs text-[#5A6472]">copie</p>
                  </div>
                </div>
                {r.shipping_address && <p className="text-sm text-[#5A6472] mb-2">Spedizione: {r.shipping_address}</p>}
                {r.notes && <p className="text-sm italic text-[#5A6472] mb-3">Note: {r.notes}</p>}
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#E5E1DA]">
                  <span className="text-xs text-[#5A6472] uppercase tracking-[0.15em]">Stato:</span>
                  {REQUEST_STATUSES.map(s => (
                    <button
                      key={s.id}
                      onClick={() => updateRequestStatus(r.id, s.id)}
                      data-testid={`req-status-btn-${r.id}-${s.id}`}
                      className="px-2 py-1 text-[10px] uppercase tracking-[0.12em] transition-opacity"
                      style={{
                        background: s.id === r.status ? s.color : "transparent",
                        color: s.id === r.status ? "#F9F8F6" : s.color,
                        border: `1px solid ${s.color}`,
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {requests.length === 0 && <p className="text-center py-10 text-[#5A6472]">Nessuna richiesta ricevuta.</p>}
        </div>
      )}
    </div>
  );
}
